/**
 * Ideas backlog ranked by RICE (reach × impact × confidence ÷ effort).
 * Anyone except viewers can submit; owners triage, and "Send to intake"
 * turns an idea into a project request.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Lightbulb, Plus, Send, Trash2 } from 'lucide-react'

import { Button } from '../ui/button'
import {
  IDEA_STATUS_LABEL, productsService, type IdeaInput, type IdeaSource, type IdeaStatus, type ProductIdea,
} from '../../services/products'
import { apiErrorMessage } from '../../utils/apiError'

const input = 'min-h-[40px] rounded-md border border-border bg-background px-3 text-sm text-foreground'
const STATUS_CLS: Record<IdeaStatus, string> = {
  new: 'bg-primary/10 text-primary', exploring: 'bg-warning/15 text-warning', planned: 'bg-success/15 text-success',
  shipped: 'bg-success/10 text-foreground', rejected: 'bg-muted text-muted-foreground',
}
const SOURCES: IdeaSource[] = ['customer', 'sales', 'support', 'internal', 'data']

interface Props {
  ideas: ProductIdea[]
  products: Array<{ id: number; name: string }>
  fixedProductId?: number
  canTriage: boolean
  canSubmit: boolean
  onChanged: () => void
}

export function IdeasTable({ ideas, products, fixedProductId, canTriage, canSubmit, onChanged }: Props) {
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState<IdeaInput>({ title: '', product_id: fixedProductId ?? null, source: 'customer' })
  const [showRejected, setShowRejected] = useState(false)

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true)
    try {
      await fn()
      if (ok) toast.success(ok)
      onChanged()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'That didn’t work. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const num = (v: string) => (v.trim() === '' ? null : Number(v))
  const submit = () => run(async () => {
    await productsService.createIdea({ ...form, product_id: fixedProductId ?? form.product_id })
    setForm({ title: '', product_id: fixedProductId ?? null, source: 'customer' })
    setAdding(false)
  }, 'Idea added')

  const visible = ideas.filter((i) => showRejected || i.status !== 'rejected')
  const hidden = ideas.length - visible.length

  return (
    <section aria-label="Ideas" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Ranked by RICE: reach × impact × confidence ÷ effort. Higher is better value for the effort.
        </p>
        {canSubmit && (
          <Button onClick={() => setAdding((v) => !v)} className="min-h-[40px] gap-2"><Plus className="h-4 w-4" /> New idea</Button>
        )}
      </div>

      {adding && (
        <div className="grid gap-3 rounded-lg border border-border bg-card p-4 md:grid-cols-2">
          <label className="text-sm text-foreground md:col-span-2">Idea
            <input className={`${input} mt-1 w-full`} value={form.title ?? ''} maxLength={255}
              onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Saved carts across devices" />
          </label>
          <label className="text-sm text-foreground md:col-span-2">Why it matters
            <textarea className={`${input} mt-1 w-full py-2`} rows={2} value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          {fixedProductId === undefined && (
            <label className="text-sm text-foreground">Product
              <select className={`${input} mt-1 w-full`} value={form.product_id ?? ''}
                onChange={(e) => setForm({ ...form, product_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">No product yet</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}
          <label className="text-sm text-foreground">Source
            <select className={`${input} mt-1 w-full`} value={form.source ?? ''}
              onChange={(e) => setForm({ ...form, source: (e.target.value || null) as IdeaSource | null })}>
              {SOURCES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>
          </label>
          <fieldset className="grid grid-cols-2 gap-3 md:col-span-2 md:grid-cols-4">
            <legend className="mb-1 text-xs text-muted-foreground">RICE (optional — you can score it later)</legend>
            <label className="text-xs text-muted-foreground">Reach (people / quarter)
              <input type="number" min={0} className={`${input} mt-1 w-full`} onChange={(e) => setForm({ ...form, reach: num(e.target.value) })} />
            </label>
            <label className="text-xs text-muted-foreground">Impact
              <select className={`${input} mt-1 w-full`} defaultValue="" onChange={(e) => setForm({ ...form, impact: num(e.target.value) })}>
                <option value="">—</option><option value="3">Massive (3)</option><option value="2">High (2)</option>
                <option value="1">Medium (1)</option><option value="0.5">Low (0.5)</option><option value="0.25">Minimal (0.25)</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground">Confidence %
              <input type="number" min={0} max={100} className={`${input} mt-1 w-full`} onChange={(e) => setForm({ ...form, confidence: num(e.target.value) })} />
            </label>
            <label className="text-xs text-muted-foreground">Effort (person-weeks)
              <input type="number" min={0.5} step={0.5} className={`${input} mt-1 w-full`} onChange={(e) => setForm({ ...form, effort: num(e.target.value) })} />
            </label>
          </fieldset>
          <div className="flex justify-end gap-2 md:col-span-2">
            <Button variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.title?.trim()}>Add idea</Button>
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
          <Lightbulb className="mx-auto mb-2 h-6 w-6" /> No ideas yet. Capture requests from customers, sales and support here.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2">Idea</th>
                {fixedProductId === undefined && <th className="px-3 py-2">Product</th>}
                <th className="px-3 py-2 text-right">RICE</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((i) => (
                <tr key={i.id} className="border-b border-border align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-foreground">{i.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {[i.source && `From ${i.source}`, i.submitted_by && `by ${i.submitted_by}`].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  {fixedProductId === undefined && <td className="px-3 py-2 text-muted-foreground">{i.product_name ?? '—'}</td>}
                  <td className="px-3 py-2 text-right">
                    <span className="font-semibold text-foreground" title={i.rice !== null
                      ? `Reach ${i.reach} × impact ${i.impact} × ${i.confidence}% ÷ ${i.effort} weeks` : 'Not scored yet'}>
                      {i.rice ?? '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {canTriage ? (
                      <select aria-label={`Status of ${i.title}`} value={i.status} disabled={busy}
                        onChange={(e) => run(() => productsService.updateIdea(i.id, { status: e.target.value as IdeaStatus }))}
                        className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground">
                        {(Object.keys(IDEA_STATUS_LABEL) as IdeaStatus[]).map((s) => <option key={s} value={s}>{IDEA_STATUS_LABEL[s]}</option>)}
                      </select>
                    ) : (
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLS[i.status]}`}>{IDEA_STATUS_LABEL[i.status]}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {i.request_id ? (
                        <Link to="/intake" className="text-xs text-primary hover:underline">In intake</Link>
                      ) : canTriage && i.status !== 'rejected' && i.status !== 'shipped' && (
                        <button type="button" disabled={busy} title="Send to project intake"
                          onClick={() => run(() => productsService.ideaToIntake(i.id), 'Sent to project intake')}
                          className="inline-flex min-h-[32px] items-center gap-1 rounded px-2 text-xs text-primary hover:bg-muted">
                          <Send className="h-3 w-3" /> Intake
                        </button>
                      )}
                      {canTriage && (
                        <button type="button" disabled={busy} aria-label={`Delete ${i.title}`}
                          onClick={() => { if (window.confirm(`Delete “${i.title}”?`)) run(() => productsService.deleteIdea(i.id), 'Idea deleted') }}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hidden > 0 && (
        <button type="button" onClick={() => setShowRejected(true)} className="text-xs text-primary hover:underline">
          Show {hidden} rejected idea{hidden === 1 ? '' : 's'}
        </button>
      )}
    </section>
  )
}
